/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DateRangeFilterConfig,
  FilterConfig,
  FilterGroupConfig,
  SearchInputFilterConfig,
} from 'components/SearchAndFilters/FilterTypes';
import {
  MultiSelectFilterConfig,
  MultiSelectRenderStyle,
} from 'components/SearchAndFilters/MultiSelect';
import { useAtom, useSetAtom } from 'jotai';
import React, { useEffect } from 'react';

import {
  filterValuesAtom,
  isQuerystringFiltersInitializedAtom,
  useOnFilterChangeResetAtoms,
} from './state';

export const useFilterConfig = (): FilterConfig => {
  const [filterValues, setFilterValues] = useAtom(filterValuesAtom);
  const setQuerystringFiltersInitialized = useSetAtom(
    isQuerystringFiltersInitializedAtom,
  );
  const resetOnFilterChange = useOnFilterChangeResetAtoms();

  useEffect(() => {
    // Only change if filter is empty or undefined
    if (!filterValues.filter) {
      setFilterValues((prevValues) => ({
        ...prevValues,
        filter: ['active'],
      }));
    }
  }, [setFilterValues]);

  // Initialize filter values from query string
  useEffect(() => {
    void setQuerystringFiltersInitialized(false);

    const queryFilters: Record<string, any> = {};
    const initQueryFilters = async () => {};

    void initQueryFilters()
      .then(() => {
        if (Object.keys(queryFilters).length > 0) {
          resetOnFilterChange();
          void setFilterValues(queryFilters);
        }
      })
      .finally(() => {
        void setQuerystringFiltersInitialized(true);
      });

    return () => {
      void setQuerystringFiltersInitialized(false);
    };
  }, []);

  const config = React.useMemo(() => {
    const newConfig = new FilterConfig({
      searchFilterId: 'search',
      rightPanelFilters: [
        'filter',
        'scan_result_status',
        'scan_status',
        'auth_status',
      ],
      leftPanelFilters: ['dateRangeFilters'],
      filters: [
        new SearchInputFilterConfig({
          id: 'search',
          label: 'Search',
          placeholder: 'Search and Filter',
          maxLength: 250,
          debounceTime: 1000,
          enableDebounce: true,
          inputProps: { type: 'text' },
          showInFilterMenu: false,
        }),
        new MultiSelectFilterConfig({
          id: 'scan_result_status',
          label: 'Scan Result Status',
          tooltip: 'Filter by web application scan result status.',
          iconClass: 'fas fa-filter',
          defaultSelectedValues: [''],
          options: [
            { value: '', label: 'All', deselectOthers: true },
            {
              value: 'NO_WEB_SERVICE',
              label: 'No Web Service',
              deselectOthers: true,
            },
            {
              value: 'PROCESSING',
              label: 'Processing',
              deselectOthers: true,
            },
            {
              value: 'SUCCESSFUL',
              label: 'Successful',
              deselectOthers: true,
            },
            {
              value: 'TIME_LIMIT_REACHED',
              label: 'Time Limit Reached',
              deselectOthers: true,
            },
            {
              value: 'SERVICE_ERROR',
              label: 'Service Error',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'scan_status',
          label: 'Scan Status',
          tooltip: 'Filter by web application scan status.',
          iconClass: 'fas fa-filter',
          defaultSelectedValues: [''],
          options: [
            { value: '', label: 'All', deselectOthers: true },
            {
              value: 'FINISHED',
              label: 'Finished',
              deselectOthers: true,
            },
            {
              value: 'CANCELED',
              label: 'Canceled',
              deselectOthers: true,
            },
            {
              value: 'RUNNING',
              label: 'Running',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'auth_status',
          label: 'Auth Status',
          tooltip: 'Filter by web application auth status.',
          iconClass: 'fas fa-filter',
          defaultSelectedValues: [''],
          options: [
            { value: '', label: 'All', deselectOthers: true },
            {
              value: 'SUCCESSFUL',
              label: 'Successful',
              deselectOthers: true,
            },
            {
              value: 'NOT_USED',
              label: 'Not Used',
              deselectOthers: true,
            },
            {
              value: 'FAILED',
              label: 'Failed',
              deselectOthers: true,
            },
            {
              value: 'NONE',
              label: 'None',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new MultiSelectFilterConfig({
          id: 'filter',
          label: 'Webapp Status',
          tooltip: 'Filter by web application status.',
          iconClass: 'fas fa-clipboard-check',
          defaultSelectedValues: [''],
          options: [
            {
              value: '',
              label: 'All',
              deselectOthers: true,
            },
            {
              value: 'active',
              label: 'Active',
              deselectOthers: true,
            },
            {
              value: 'inactive',
              label: 'Inactive',
              deselectOthers: true,
            },
            {
              value: 'deleted',
              label: 'Deleted (Aged Out or Manual)',
              deselectOthers: true,
            },
          ],
          renderStyle: MultiSelectRenderStyle.Badges,
        }),
        new FilterGroupConfig({
          id: 'dateRangeFilters',
          label: 'Filter by Date',
          iconClass: 'fas fa-calendar-alt',
          tooltip:
            'Filter by date and timestamps. The From and To dates are inclusive.',
          filters: [
            new DateRangeFilterConfig({
              id: 'latest_scan__launch_datetime',
              label: 'Last Scanned Date',
              labelWidth: '150px',
            }),
            new DateRangeFilterConfig({
              id: 'last_deactivated_at',
              label: 'Last Deactivated Date',
              labelWidth: '150px',
            }),
            new DateRangeFilterConfig({
              id: 'last_reactivated_at',
              label: 'Last Reactivated Date',
              labelWidth: '150px',
            }),
            new DateRangeFilterConfig({
              id: 'created_at',
              label: 'Created Date',
              labelWidth: '150px',
            }),
            new DateRangeFilterConfig({
              id: 'updated_at',
              label: 'Updated Date',
              labelWidth: '150px',
            }),
          ],
        }),
      ],
    });

    // Apply initial filter values
    newConfig.applyInitialFilterValues(filterValues);

    return newConfig;
  }, [filterValues]);

  return config;
};
